// src/context/AuthContext.js (dashboard)
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, billingAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [subscription, setSubscription] = useState(null);
    const [subLoading, setSubLoading] = useState(false);

    // Fetch subscription status for a company — called after login and on demand
    const fetchSubscription = useCallback(async (companyId, role) => {
        // GAAF admins are never blocked
        if (!companyId || role === 'gaaf_admin') {
            setSubscription({ status: 'active', plan: 'enterprise' });
            return;
        }
        setSubLoading(true);
        try {
            const res = await billingAPI.getSubscription({ companyId });
            setSubscription(res.data);
        } catch (_) {
            // On error default to allowing access — don't lock out on API failure
            setSubscription({ status: 'active', plan: null });
        }
        setSubLoading(false);
    }, []);

    // Restore session on mount
    useEffect(() => {
        const token = localStorage.getItem('gaafrfid_token');
        const stored = localStorage.getItem('gaafrfid_user');
        if (token && stored) {
            try {
                const userData = JSON.parse(stored);
                setUser(userData);
                fetchSubscription(userData.companyId, userData.role);
            } catch (_) { }
        }
        setLoading(false);
    }, []);

    const login = useCallback(async (email, password) => {
        const res = await authAPI.login(email, password);
        const { accessToken, user: userData } = res.data;
        localStorage.setItem('gaafrfid_token', accessToken);
        localStorage.setItem('gaafrfid_user', JSON.stringify(userData));
        setUser(userData);
        await fetchSubscription(userData.companyId, userData.role);
        return userData;
    }, [fetchSubscription]);

    const logout = useCallback(() => {
        localStorage.removeItem('gaafrfid_token');
        localStorage.removeItem('gaafrfid_user');
        setUser(null);
        setSubscription(null);
    }, []);

    const isGaafAdmin = user?.role === 'gaaf_admin';
    const isCompanyAdmin = user?.role === 'company_admin';
    const companyId = user?.companyId;

    // True if the company has an active subscription or is GAAF admin
    const isSubscriptionActive = isGaafAdmin ||
        subscription?.status === 'active' ||
        subLoading; // don't block while still loading

    // True if blocked — past_due or no plan and not admin
    const isSubscriptionBlocked = !isGaafAdmin &&
        !subLoading &&
        subscription !== null &&
        subscription?.status !== 'active';

    return (
        <AuthContext.Provider value={{
            user, loading, login, logout,
            isGaafAdmin, isCompanyAdmin, companyId,
            subscription, subLoading,
            isSubscriptionActive, isSubscriptionBlocked,
            refreshSubscription: () => fetchSubscription(companyId, user?.role),
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};
